import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Sparkles, GraduationCap, Calendar, IndianRupee, Camera, FileText, Info, Check, Phone, MapPin, Mail, ArrowRight, Award, Users, Clock, Star, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const courses = [
  { 
    id: "course1", 
    name: "Only Makeup", 
    fees: 28000, 
    duration: "30 days",
    description: "Master the art of professional makeup with comprehensive training in base makeup, eye makeup, contouring, highlighting, and bridal makeup techniques.",
    curriculum: ["Base Makeup Fundamentals", "Eye Makeup Techniques", "Contouring & Highlighting", "Bridal Makeup", "Party Makeup", "Professional Makeup Practice"]
  },
  { 
    id: "course2", 
    name: "Makeup & Hair", 
    fees: 30000, 
    duration: "45 days",
    description: "Complete transformation course covering both makeup artistry and professional hair styling including braiding, buns, and bridal hairstyles.",
    curriculum: ["All Makeup Modules", "Hair Styling Basics", "Braiding Techniques", "Bun Styles", "Bridal Hair Styling", "Advanced Hair Techniques"]
  },
  { 
    id: "course3", 
    name: "Makeup, Hair, Nails or Draping", 
    fees: 35000, 
    duration: "60 days",
    description: "Comprehensive beauty course with makeup, hair styling, and your choice of professional nail art or traditional draping techniques.",
    curriculum: ["All Makeup & Hair Modules", "Nail Art OR Draping (Your Choice)", "Professional Portfolio Building", "Client Handling"]
  },
  { 
    id: "course4", 
    name: "Makeup, Hair, Nails & Draping", 
    fees: 39500, 
    duration: "60 days",
    description: "Complete professional makeup artist course covering all aspects - makeup, hair, nails, and traditional draping for a complete skill set.",
    curriculum: ["All Makeup Modules", "Complete Hair Styling", "Professional Nail Art", "Saree & Dupatta Draping", "Advanced Techniques", "Business Setup Guidance"]
  }
];

const faqs = [
  { q: "What are the eligibility criteria for admission?", a: "Anyone with a passion for makeup can join. No prior experience required. Minimum age 16 years." },
  { q: "Do you provide makeup kits?", a: "Students need to purchase their own professional makeup kit as per our recommended list. Basic materials are provided for initial practice." },
  { q: "Will I get a certificate after completion?", a: "Yes, you'll receive a MUAS Academy certificate upon successful completion with minimum 75% attendance." },
  { q: "Can I pay fees in installments?", a: "Yes, we offer flexible installment plans. Contact admin for details." },
  { q: "Are there job placements after the course?", a: "We provide guidance and support for freelancing and salon placements based on your performance." }
];

const sliderImages = [
  "DSC00231.JPG", "DSC00325.JPG", "DSC02616.JPG", "DSC02636.JPG", "DSC02637.JPG",
  "DSC03863.JPG", "DSC03882.JPG", "DSC03906.JPG", "DSC05766.JPG", "DSC06708.JPG",
  "DSC07372.JPG", "DSC07483.JPG", "DSC07681.JPG", "DSC09392.JPG", "DSC09453.JPG",
  "IMG_1015.JPG", "IMG_1026.JPG", "IMG_1177.JPG", "_DSC5004.JPG", "_DSC5012.JPG"
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [activeTab, setActiveTab] = useState("student");
  const [isRegistering, setIsRegistering] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const aadhaarInputRef = useRef(null);
  const photoInputRef = useRef(null);

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  // Registration form - Simplified
  const [regForm, setRegForm] = useState({
    name: "",
    mobile: "",
    course_id: "",
    course3_choice: "",
    aadhaar_front_base64: "",
    aadhaar_back_base64: ""
  });
  
  const aadhaarFrontRef = useRef(null);
  const aadhaarBackRef = useRef(null);

  // Login forms
  const [studentLogin, setStudentLogin] = useState({ mobile: "", password: "" });
  const [adminLogin, setAdminLogin] = useState({ user_id: "", password: "" });
  const [teacherLogin, setTeacherLogin] = useState({ teacher_id: "", password: "" });

  const handleFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleAadhaarUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      const base64 = await handleFileToBase64(file);
      setRegForm({ ...regForm, aadhaar_photo_base64: base64 });
      toast.success("Aadhaar photo uploaded");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      const base64 = await handleFileToBase64(file);
      setRegForm({ ...regForm, passport_photo_base64: base64 });
      toast.success("Passport photo uploaded");
    }
  };

  const handleStudentRegister = async (e) => {
    e.preventDefault();
    
    if (!regForm.aadhaar_front_base64) {
      toast.error("Please upload Aadhaar card front photo");
      return;
    }
    if (!regForm.aadhaar_back_base64) {
      toast.error("Please upload Aadhaar card back photo");
      return;
    }
    if (regForm.course_id === "course3" && !regForm.course3_choice) {
      toast.error("Please choose between Nails or Draping for Course 3");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please read and accept terms and conditions");
      return;
    }

    try {
      const response = await axios.post(`${API}/student/register`, {
        ...regForm,
        terms_accepted: termsAccepted
      });
      toast.success(response.data.message);
      setIsRegistering(false);
      setShowLogin(false);
      setRegForm({
        name: "", mobile: "", course_id: "", course3_choice: "", 
        aadhaar_front_base64: "", aadhaar_back_base64: ""
      });
      setTermsAccepted(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    }
  };

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/student/login`, studentLogin);
      localStorage.setItem("student", JSON.stringify(response.data.student));
      toast.success("Login successful!");
      navigate("/student");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/admin/login`, adminLogin);
      localStorage.setItem("admin", "true");
      toast.success("Admin login successful!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid admin credentials");
    }
  };

  const handleTeacherLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/teacher/login`, teacherLogin);
      localStorage.setItem("teacher", JSON.stringify(response.data.teacher));
      toast.success("Teacher login successful!");
      navigate("/teacher");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid teacher credentials");
    }
  };

  const openCourseDetails = (course) => {
    setSelectedCourse(course);
    setShowCourseDetails(true);
  };

  const handleJoinNow = (courseId) => {
    setRegForm({ ...regForm, course_id: courseId });
    setIsRegistering(true);
    setShowLogin(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-pink-100 via-purple-50 to-white py-20">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-6 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border-2 border-pink-300 shadow-lg">
              <Award className="w-5 h-5 text-pink-600" />
              <span className="text-base font-semibold text-pink-900">Professional Makeup Academy in Ranchi</span>
            </div>
            
            {/* MUAS Academy Logo */}
            <div className="mb-6">
              <div className="inline-block p-6 bg-gradient-to-br from-pink-500 via-purple-500 to-pink-600 rounded-3xl shadow-2xl">
                <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-wider" style={{fontFamily: "'Playfair Display', serif"}}>
                  MUAS
                </h1>
              </div>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              MUAS Academy
            </h1>
            <p className="text-xl sm:text-2xl text-gray-700 max-w-3xl mx-auto mb-8 leading-relaxed">
              Transform Your Passion into a Professional Career with Ranchi's Premier Makeup, Hair, Nails & Draping Training Institute
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="font-semibold">500+ Students Trained</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow">
                <Star className="w-5 h-5 text-yellow-500" />
                <span className="font-semibold">Expert Mentors</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow">
                <Award className="w-5 h-5 text-green-600" />
                <span className="font-semibold">Certified Courses</span>
              </div>
            </div>
          </div>

          {/* Image Slider */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              {sliderImages.map((img, idx) => (
                <div
                  key={idx}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    idx === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <img
                    src={`/slider/${img}`}
                    alt={`MUAS Academy ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              
              {/* Slider Controls */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition"
                data-testid="slider-prev"
              >
                <ChevronLeft className="w-6 h-6 text-gray-800" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-3 rounded-full shadow-lg transition"
                data-testid="slider-next"
              >
                <ChevronRight className="w-6 h-6 text-gray-800" />
              </button>

              {/* Dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {sliderImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-2 h-2 rounded-full transition ${
                      idx === currentSlide ? 'bg-white w-8' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Login Button */}
          <div className="text-center">
            <Button 
              onClick={() => setShowLogin(true)} 
              size="lg" 
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-lg px-8 py-6"
              data-testid="login-here-button"
            >
              Login Here <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Courses Section */}
      <div className="container mx-auto px-4 py-16 bg-white">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">Our Professional Makeup & Beauty Courses</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Choose from our range of professional courses designed to make you job-ready in the beauty industry
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {courses.map((course, idx) => {
            // Define unique icons and colors for each course
            const courseIcons = [
              { emoji: "💄", gradient: "from-pink-500 to-rose-600" },
              { emoji: "💇", gradient: "from-purple-500 to-pink-600" },
              { emoji: "💅", gradient: "from-indigo-500 to-purple-600" },
              { emoji: "👗", gradient: "from-fuchsia-500 to-pink-600" }
            ];
            const courseIcon = courseIcons[idx] || courseIcons[0];
            
            return (
            <Card key={course.id} className="hover:shadow-xl transition-all duration-300 border-2 hover:border-pink-400 bg-white" data-testid={`course-card-${idx}`}>
              <CardHeader className="pb-4">
                <div className={`w-16 h-16 bg-gradient-to-br ${courseIcon.gradient} rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg`}>
                  <span className="text-4xl">{courseIcon.emoji}</span>
                </div>
                <CardTitle className="text-xl text-center mb-2">{course.name}</CardTitle>
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <IndianRupee className="w-4 h-4" />
                    <span className="font-bold text-2xl text-pink-600">₹{course.fees.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span className="font-semibold">{course.duration}</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-600 text-center line-clamp-3">{course.description}</p>
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full border-2 border-purple-500 text-purple-600 hover:bg-purple-50"
                    onClick={() => openCourseDetails(course)}
                    data-testid={`view-details-${idx}`}
                  >
                    <Info className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                  <Button
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700"
                    onClick={() => handleJoinNow(course.id)}
                    data-testid={`join-now-${idx}`}
                  >
                    Join Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      </div>

      {/* Certification Strip - Right to Left */}
      <div className="bg-white border-y border-gray-200 py-8 overflow-hidden">
        <div className="flex items-center">
          <div className="scroll-left flex gap-16 items-center">
            <div className="logo-strip">
              {/* ISO Certification */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">ISO</span>
                </div>
                <span className="text-xs text-gray-600 font-semibold">ISO Certified</span>
              </div>
              
              {/* IAF Accreditation */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 bg-gradient-to-br from-green-600 to-green-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">IAF</span>
                </div>
                <span className="text-xs text-gray-600 font-semibold">IAF Accredited</span>
              </div>
              
              {/* MSME */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 bg-gradient-to-br from-orange-600 to-orange-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-sm text-center px-2">MSME</span>
                </div>
                <span className="text-xs text-gray-600 font-semibold">MSME Certified</span>
              </div>
            </div>
            
            {/* Duplicate for seamless loop */}
            <div className="logo-strip">
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">ISO</span>
                </div>
                <span className="text-xs text-gray-600 font-semibold">ISO Certified</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 bg-gradient-to-br from-green-600 to-green-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">IAF</span>
                </div>
                <span className="text-xs text-gray-600 font-semibold">IAF Accredited</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-24 h-24 bg-gradient-to-br from-orange-600 to-orange-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-sm text-center px-2">MSME</span>
                </div>
                <span className="text-xs text-gray-600 font-semibold">MSME Certified</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partner Brands Strip - Left to Right */}
      <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-y border-gray-200 py-8 overflow-hidden">
        <div className="flex items-center">
          <div className="scroll-right flex gap-16 items-center">
            <div className="logo-strip">
              {/* NARS */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-2xl font-bold tracking-wider" style={{fontFamily: 'serif'}}>NARS</span>
                </div>
              </div>
              
              {/* Huda Beauty */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-xl font-bold">Huda Beauty</span>
                </div>
              </div>
              
              {/* Pat McGrath Labs */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-base font-bold text-center">Pat McGrath</span>
                </div>
              </div>
              
              {/* Sephora */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-black rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-2xl font-bold text-white">SEPHORA</span>
                </div>
              </div>
              
              {/* Charlotte Tilbury */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-sm font-bold text-center">Charlotte Tilbury</span>
                </div>
              </div>
              
              {/* Fenty Beauty */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-xl font-bold">FENTY</span>
                </div>
              </div>
              
              {/* Armani Beauty */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-lg font-bold">ARMANI</span>
                </div>
              </div>
              
              {/* Givenchy */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-xl font-bold tracking-wide">GIVENCHY</span>
                </div>
              </div>
            </div>
            
            {/* Duplicate for seamless loop */}
            <div className="logo-strip">
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-2xl font-bold tracking-wider" style={{fontFamily: 'serif'}}>NARS</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-xl font-bold">Huda Beauty</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-base font-bold text-center">Pat McGrath</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-black rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-2xl font-bold text-white">SEPHORA</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-sm font-bold text-center">Charlotte Tilbury</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-xl font-bold">FENTY</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-lg font-bold">ARMANI</span>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <div className="w-32 h-20 bg-white rounded-lg shadow-md flex items-center justify-center px-4">
                  <span className="text-xl font-bold tracking-wide">GIVENCHY</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Google Reviews Section */}
      <div className="container mx-auto px-4 py-16 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">What Our Students Say</h2>
          <p className="text-lg text-gray-600 mb-8">Read reviews from our satisfied students</p>
          
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
              </div>
              <p className="text-2xl font-bold text-gray-900">4.9/5.0 Rating</p>
              <p className="text-gray-600">Based on 100+ Google Reviews</p>
            </div>
            
            <a 
              href="https://share.google/NAcF8xSh8i6TxXl73" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button 
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-6"
              >
                <Star className="w-5 h-5 mr-2" />
                View All Google Reviews
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
            
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex gap-1 mb-2 justify-center">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-sm text-gray-700 italic">"Best makeup academy in Ranchi! Learned so much from expert teachers."</p>
              </div>
              <div className="p-4 bg-pink-50 rounded-lg">
                <div className="flex gap-1 mb-2 justify-center">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-sm text-gray-700 italic">"Professional course with hands-on training. Highly recommended!"</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex gap-1 mb-2 justify-center">
                  {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-sm text-gray-700 italic">"Great mentorship and support. Started my own makeup business after course!"</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEO Content Section */}
      <div className="container mx-auto px-4 py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6">Best Makeup Courses in Ranchi - Learn Professional Makeup, Hair Styling, Nails & Draping</h2>
          <p className="text-lg text-gray-700 mb-8 leading-relaxed">
            MUAS Academy is Ranchi's leading professional makeup training institute offering comprehensive courses in makeup artistry, hair styling, nail art, and traditional draping techniques. Whether you want to become a professional makeup artist, hair stylist, or start your own beauty business, our expert-led courses provide hands-on training with industry-standard techniques and personalized mentorship.
          </p>

          <h3 className="text-2xl font-bold mb-4">Why Choose MUAS Academy for Makeup Courses in Ranchi?</h3>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="flex gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Expert Mentorship</h4>
                <p className="text-gray-600">Learn from industry professionals with years of experience</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Hands-on Training</h4>
                <p className="text-gray-600">Practical sessions with real clients and models</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Flexible Schedules</h4>
                <p className="text-gray-600">Classes designed to fit your lifestyle</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Certification</h4>
                <p className="text-gray-600">Recognized certificate upon course completion</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Job Placement Support</h4>
                <p className="text-gray-600">Guidance for salon placements and freelancing</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Affordable Fees</h4>
                <p className="text-gray-600">Quality education at competitive prices with EMI options</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} className="border rounded-lg px-6 bg-white">
                <AccordionTrigger className="text-left font-semibold hover:text-pink-600">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* About */}
            <div>
              <h3 className="text-2xl font-bold mb-4">MUAS Academy</h3>
              <p className="text-gray-300 mb-4">
                Ranchi's premier makeup and beauty training institute. Empowering aspiring artists with professional skills since our inception.
              </p>
              <div className="flex gap-4">
                <Award className="w-5 h-5 text-pink-400" />
                <span className="text-sm">Certified Courses</span>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Us</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-pink-400 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">C-127, Harmu Housing Colony, Harmu, Ranchi</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-pink-400" />
                  <span className="text-gray-300">+91 83401 82227</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-pink-400" />
                  <span className="text-gray-300">info@muasacademy.com</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-4">Important Links</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => setShowTerms(true)} 
                  className="block text-gray-300 hover:text-pink-400 transition"
                >
                  Terms & Conditions
                </button>
                <a href="#privacy" className="block text-gray-300 hover:text-pink-400 transition">
                  Privacy Policy
                </a>
                <a href="#refund" className="block text-gray-300 hover:text-pink-400 transition">
                  Refund Policy
                </a>
                <button 
                  onClick={() => setShowLogin(true)} 
                  className="block text-gray-300 hover:text-pink-400 transition"
                >
                  Student Login
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 text-center">
            <p className="text-gray-400">
              © 2025 MUAS Academy. All rights reserved. | Best Makeup Academy in Ranchi | Professional Makeup Courses | Hair Styling Courses | Nail Art Training
            </p>
          </div>
        </div>
      </footer>

      {/* Course Details Modal */}
      <Dialog open={showCourseDetails} onOpenChange={setShowCourseDetails}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="course-details-modal">
          {selectedCourse && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl">{selectedCourse.name}</DialogTitle>
                <DialogDescription className="text-lg">
                  {selectedCourse.duration} | ₹{selectedCourse.fees.toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3">Course Overview</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedCourse.description}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3">Curriculum</h3>
                  <div className="space-y-2">
                    {selectedCourse.curriculum.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-800">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-pink-50 to-purple-50 p-6 rounded-lg">
                  <h3 className="text-xl font-bold mb-3">What You'll Get</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Hands-on practical training</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Personal mentor support</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Certification upon completion</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Portfolio building assistance</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Job placement guidance</li>
                  </ul>
                </div>

                <Button 
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-lg py-6"
                  onClick={() => {
                    setShowCourseDetails(false);
                    handleJoinNow(selectedCourse.id);
                  }}
                  data-testid="join-from-modal"
                >
                  Enroll in {selectedCourse.name} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="max-w-md" data-testid="login-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center">Login Here</DialogTitle>
            <DialogDescription className="text-center">Access your account or register as new student</DialogDescription>
          </DialogHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="student" data-testid="student-tab">Student</TabsTrigger>
              <TabsTrigger value="teacher" data-testid="teacher-tab">Teacher</TabsTrigger>
              <TabsTrigger value="admin" data-testid="admin-tab">Admin</TabsTrigger>
            </TabsList>

            <TabsContent value="student">
              {!isRegistering ? (
                <form onSubmit={handleStudentLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="student-mobile">Mobile Number</Label>
                    <Input
                      id="student-mobile"
                      data-testid="student-login-mobile"
                      type="tel"
                      placeholder="Enter your mobile"
                      value={studentLogin.mobile}
                      onChange={(e) => setStudentLogin({ ...studentLogin, mobile: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="student-password">Password</Label>
                    <Input
                      id="student-password"
                      data-testid="student-login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={studentLogin.password}
                      onChange={(e) => setStudentLogin({ ...studentLogin, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700" data-testid="student-login-button">
                    Login
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsRegistering(true)}
                    data-testid="show-register-button"
                  >
                    New Student? Register Here
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleStudentRegister} className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                  <div>
                    <Label htmlFor="reg-name">Full Name (as per Aadhaar Card) *</Label>
                    <Input
                      id="reg-name"
                      data-testid="register-name"
                      placeholder="Enter full name as per Aadhaar"
                      value={regForm.name}
                      onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-mobile">WhatsApp Number *</Label>
                    <Input
                      id="reg-mobile"
                      data-testid="register-mobile"
                      type="tel"
                      placeholder="Enter WhatsApp number"
                      value={regForm.mobile}
                      onChange={(e) => setRegForm({ ...regForm, mobile: e.target.value })}
                      required
                      maxLength={10}
                    />
                    <p className="text-xs text-gray-500 mt-1">This will be your login password</p>
                  </div>
                  <div>
                    <Label htmlFor="reg-course">Select Course *</Label>
                    <Select value={regForm.course_id} onValueChange={(val) => setRegForm({ ...regForm, course_id: val, course3_choice: "" })} required>
                      <SelectTrigger data-testid="register-course-select">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id} data-testid={`course-option-${course.id}`}>
                            {course.name} - ₹{course.fees.toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {regForm.course_id === "course3" && (
                    <div>
                      <Label htmlFor="course3-choice">Choose Module for Course 3 *</Label>
                      <Select value={regForm.course3_choice} onValueChange={(val) => setRegForm({ ...regForm, course3_choice: val })} required>
                        <SelectTrigger data-testid="course3-choice-select">
                          <SelectValue placeholder="Choose between Nails or Draping" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nails" data-testid="choice-nails">Nails</SelectItem>
                          <SelectItem value="draping" data-testid="choice-draping">Draping</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Upload Aadhaar Card Front *</Label>
                    <input
                      ref={aadhaarFrontRef}
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const base64 = await handleFileToBase64(file);
                          setRegForm({ ...regForm, aadhaar_front_base64: base64 });
                        }
                      }}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => aadhaarFrontRef.current?.click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {regForm.aadhaar_front_base64 ? "Front Uploaded ✓" : "Upload Aadhaar Front"}
                    </Button>
                  </div>
                  <div>
                    <Label>Upload Aadhaar Card Back *</Label>
                    <input
                      ref={aadhaarBackRef}
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const base64 = await handleFileToBase64(file);
                          setRegForm({ ...regForm, aadhaar_back_base64: base64 });
                        }
                      }}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => aadhaarBackRef.current?.click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {regForm.aadhaar_back_base64 ? "Back Uploaded ✓" : "Upload Aadhaar Back"}
                    </Button>
                  </div>
                  <div className="bg-blue-50 p-3 rounded">
                    <p className="text-sm text-gray-700">
                      <strong>Note:</strong> Your WhatsApp number will be used as your password for login. Other details will be filled by admin on Day 1.
                    </p>
                  </div>
                  <div>
                    <Label>PASSWORD (AUTO-FILLED) *</Label>
                    <Input
                      type="text"
                      value={regForm.mobile}
                      disabled
                      className="bg-gray-100"
                      placeholder="Will be same as WhatsApp number"
                    />
                    <p className="text-xs text-gray-500 mt-1">Password cannot be changed during registration</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="terms"
                      checked={termsAccepted}
                      onCheckedChange={setTermsAccepted}
                      data-testid="terms-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="terms" className="text-sm cursor-pointer">
                        I accept the{" "}
                        <button
                          type="button"
                          onClick={() => setShowTerms(true)}
                          className="text-pink-600 underline"
                          data-testid="view-terms-button"
                        >
                          Terms and Conditions
                        </button>
                      </Label>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700" data-testid="register-submit-button">
                    Register
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsRegistering(false)}
                    data-testid="back-to-login-button"
                  >
                    Back to Login
                  </Button>
                </form>
              )}
            </TabsContent>

            <TabsContent value="teacher">
              <form onSubmit={handleTeacherLogin} className="space-y-4">
                <div>
                  <Label htmlFor="teacher-id">Teacher ID</Label>
                  <Input
                    id="teacher-id"
                    data-testid="teacher-id"
                    placeholder="Enter teacher ID"
                    value={teacherLogin.teacher_id}
                    onChange={(e) => setTeacherLogin({ ...teacherLogin, teacher_id: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="teacher-password">Password</Label>
                  <Input
                    id="teacher-password"
                    data-testid="teacher-password"
                    type="password"
                    placeholder="Enter password"
                    value={teacherLogin.password}
                    onChange={(e) => setTeacherLogin({ ...teacherLogin, password: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700" data-testid="teacher-login-button">
                  Teacher Login
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="admin">
              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div>
                  <Label htmlFor="admin-id">Admin User ID</Label>
                  <Input
                    id="admin-id"
                    data-testid="admin-userid"
                    placeholder="Enter admin user ID"
                    value={adminLogin.user_id}
                    onChange={(e) => setAdminLogin({ ...adminLogin, user_id: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    data-testid="admin-password"
                    type="password"
                    placeholder="Enter admin password"
                    value={adminLogin.password}
                    onChange={(e) => setAdminLogin({ ...adminLogin, password: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700" data-testid="admin-login-button">
                  Admin Login
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Terms and Conditions Dialog - Same as before */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-4xl max-h-[80vh]" data-testid="terms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Terms and Conditions - MUAS Academy
            </DialogTitle>
            <DialogDescription>Please read all terms carefully before registration</DialogDescription>
          </DialogHeader>
          <div className="h-[500px] overflow-y-auto border rounded p-6 bg-gray-50 text-sm" data-testid="terms-text">
            <div className="space-y-4">
              <h3 className="font-bold text-lg">GENERAL TERMS AND CONDITIONS</h3>
              
              <div>
                <h4 className="font-semibold mb-2">1. ADMISSION AND REGISTRATION</h4>
                <p>1.1 All students must complete the registration form with accurate personal information.</p>
                <p>1.2 Students must upload valid Aadhaar card and passport-size photograph during registration.</p>
                <p>1.3 Registration is subject to availability of seats in the chosen course.</p>
                <p>1.4 MUAS Academy reserves the right to reject any application without providing reasons.</p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">2. COURSE FEES AND PAYMENT</h4>
                <p>2.1 Course fees must be paid as per the payment schedule provided.</p>
                <p>2.2 Registration fees are non-refundable once paid.</p>
                <p>2.3 Students must submit payment screenshots after making payment via the provided QR code.</p>
                <p>2.4 Full course fees must be paid before course commencement or as per installment plan.</p>
                <p>2.5 Late payment may result in denial of class attendance.</p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">3. ATTENDANCE POLICY</h4>
                <p>3.1 Students must maintain minimum 75% attendance to be eligible for certificate.</p>
                <p>3.2 Attendance can only be marked within 50 meters radius of academy premises using the app.</p>
                <p>3.3 Students must mark attendance daily when present at academy.</p>
                <p>3.4 Proxy attendance or fraudulent marking is strictly prohibited and may lead to expulsion.</p>
              </div>

              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="font-semibold mb-2">DECLARATION:</p>
                <p>By accepting these terms and conditions, I confirm that:</p>
                <p>• I have read and understood all the terms mentioned above</p>
                <p>• I agree to abide by all rules and regulations of MUAS Academy</p>
                <p>• All information provided by me is true and accurate</p>
                <p>• I accept the consequences of violating any terms</p>
              </div>
            </div>
          </div>
          <Button onClick={() => setShowTerms(false)} className="w-full" data-testid="close-terms-button">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}